// Onboarding wizard layout — progress-indicator + step content + navigation.
// PRD-06 §3 (9 stappen). Mobile-first.

import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface Step {
  num: number;
  labelKey: string;
  default: string;
  optional?: boolean;
}

const STEPS: Step[] = [
  { num: 1, labelKey: "onboarding.step.welcome", default: "Welkom" },
  { num: 2, labelKey: "onboarding.step.personal", default: "Gegevens" },
  { num: 3, labelKey: "onboarding.step.bank", default: "Bank" },
  { num: 4, labelKey: "onboarding.step.providers", default: "Providers" },
  { num: 5, labelKey: "onboarding.step.email", default: "E-mail" },
  { num: 6, labelKey: "onboarding.step.analysis", default: "Analyse" },
  { num: 7, labelKey: "onboarding.step.review", default: "Review" },
  { num: 8, labelKey: "onboarding.step.cjib", default: "CJIB", optional: true },
  { num: 9, labelKey: "onboarding.step.verify", default: "Bevestig" },
];

interface OnboardingLayoutProps {
  currentStep: number;
  completedSteps: Set<number>;
  children: ReactNode;
}

export function OnboardingLayout({ currentStep, completedSteps, children }: OnboardingLayoutProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <header className="bg-white border-b border-neutral-200 px-4 sm:px-8 py-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-lg font-semibold">{t("onboarding.title", "Welkom bij Balans Buddy")}</h1>
          <ProgressBar currentStep={currentStep} completedSteps={completedSteps} />
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-2xl mx-auto">{children}</div>
      </main>
    </div>
  );
}

function ProgressBar({
  currentStep,
  completedSteps,
}: {
  currentStep: number;
  completedSteps: Set<number>;
}) {
  const { t } = useTranslation();
  return (
    <ol className="mt-3 flex items-center gap-1 overflow-x-auto" aria-label={t("onboarding.progress", "Voortgang")}>
      {STEPS.map((s) => {
        const isActive = s.num === currentStep;
        const isDone = completedSteps.has(s.num);
        return (
          <li
            key={s.num}
            className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
              isActive
                ? "bg-brand-50 text-brand-700 font-medium"
                : isDone
                  ? "text-green-700"
                  : "text-neutral-400"
            } ${s.optional ? "border border-dashed border-neutral-300" : ""}`}
            aria-current={isActive ? "step" : undefined}
          >
            <span
              className={`w-5 h-5 inline-flex items-center justify-center rounded-full text-[10px] ${
                isActive ? "bg-brand-500 text-white" : isDone ? "bg-green-500 text-white" : "bg-neutral-200"
              }`}
            >
              {isDone ? "✓" : s.num}
            </span>
            <span className="hidden sm:inline">{t(s.labelKey, s.default)}</span>
          </li>
        );
      })}
    </ol>
  );
}
